import { getUserIdFromToken, getGuestId, getSessionId, setGuestId, setSessionId, isLoggedIn, generateSessionId, getGoalLevel } from "@/utils/storage";
import { getStoredGuestExamUser } from "@/api/exam";
import {
  REALTIME_CALLS_URL,
  DEFAULT_REALTIME_MODEL,
  buildTask2RealtimeTools,
  buildTask2TopicExtra,
  buildGaSessionUpdatePayload,
  extractRealtimeClientSecret,
  extractRealtimeModel,
  maskSecret,
} from './realtimeGa';
import {
  shouldClearContinuationRearmBlock,
  shouldCreateResponseForToolAck,
  shouldDropLearnerSpeechDuringAssistantAudio,
  shouldScheduleContinuationPrompt,
  shouldSuppressNewExaminerSpeech,
} from '@/lib/speaking/exam/live/task2TurnTaking';
import { captureVoiceAgentInitTopicId, TASK2_LOCAL_STORAGE_TOPIC_KEY } from '@/lib/speaking/exam/live/task2PersistenceTopic';
import {
  claimTask2Ending,
  isTask2EndingClaimed,
  logTask2Ending,
} from '@/lib/speaking/exam/live/task2EndingAuthority';
import { playTrackedLiveExamAudio } from '@/lib/speaking/exam/live/liveExamRuntimeAudio';
import { isLiveExamRuntimeExiting } from '@/lib/speaking/exam/live/liveExamRuntimeTeardown';

export interface VoiceAgentConfig {
  ephemeralUrl: string;
  model?: string;
  voice?: string;
  sessionId?: string;
  userId?: string;
  topicId?: string;
}

export interface VoiceAgentCallbacks {
  onStatusChange?: (status: string) => void;
  onError?: (err: string) => void;
  onAIResponse?: (response: { text: string; audio?: string }) => void;
  onAIStreamDelta?: (partialText: string) => void;
  onAssistantCompleted?: () => void;
  onInitialData?: (data: { topic?: string; topicId?: string; systemPrompt?: string; guardrails?: string; topicAudio?: string }) => void;
  onTaskCompleted?: (info?: { playedAudio?: boolean; topicId?: string }) => void;
  onLiveTranscriptUpdate?: (t: string) => void;
  onTranscriptUpdate?: (t: string) => void;
  onHistoryUpdated?: (history: Array<{ role: 'user' | 'assistant'; text: string }>) => void;
  onNavigate?: (path: string) => void;
}

class VoiceAgentClient {
  private config: VoiceAgentConfig; // Make config public for debugging
  private callbacks: VoiceAgentCallbacks;

  private audioStream: MediaStream | null = null;
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private remoteAudioEl: HTMLAudioElement | null = null;

  private fullTranscript = "";
  private liveTranscript = "";
  private lastSpeechTime = 0;
  private currentTextBuffer = "";
  private aiResponsePending = false;
  private blockNormalReplies: boolean = false;

  private pendingToolCalls: Record<string, { name: string; args: string }> = {};
  private toolContinuePromptTimer: number | null = null;
  private toolTaskEndingTimer: number | null = null;
  private assistantCompletedAt: number | null = null;
  private remainingSeconds: number = 0;
  private lastAssistantOutputKind: 'none' | 'normal' | 'continue_prompt' | 'ending' = 'none';
  private isAssistantAudioActive: boolean = false;
  private isLocalAudioActive: boolean = false;
  private awaitingPostContinuationDecision: boolean = false;
  private postContinuationWindowId: number = 0;
  private responseWatchdogTimer: number | null = null;
  private assistantAudioWatchdogTimer: number | null = null;
  private assistantAudioEndedDelayTimer: number | null = null;
  // Debounce user turn end to avoid replying mid-speech on long utterances
  private userResponseDebounceTimer: number | null = null;
  private pendingUserFinalText: string = "";

  private conversation: Array<{ role: 'user' | 'assistant'; text: string }> = [];

  // Model and metadata from server
  private sessionModelFromServer: string | null = null;
  private initialTopic?: string;
  private initialTopicId?: string;
  private initialSystemPrompt?: string;
  private initialGuardrails?: string;

  // Task completion data
  private taskTopicId?: string;

  // End-state guards
  private ended: boolean = false;
  private taskCompleted: boolean = false;

  // Continuation prompt control and local audio playback
  private continuePromptTriggered: boolean = false;
  private continuePromptUsedOnce: boolean = false; // NEW: Track if continuation was used once
  private continuePromptFullyPlayed: boolean = false; // NEW: Track if continuation was fully played
  private continuationRearmBlocked: boolean = false;
  private localPromptAudioEl: HTMLAudioElement | null = null;
  private localPromptAudioSource: AudioBufferSourceNode | null = null;
  private silenceGenerationId: number = 0;
  private pendingTextForStorage: string = "";
  private firstAiAudioLogged = false;
  private realtimeModel = DEFAULT_REALTIME_MODEL;
  private processedToolCallIds = new Set<string>();

  constructor(config: VoiceAgentConfig, callbacks: VoiceAgentCallbacks = {}) {
    this.config = config;
    this.callbacks = callbacks;
  }

  async initializeMic(): Promise<boolean> {
    try {
      this.audioStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: false // Ensure gain control is on
        }
      });
      this.callbacks.onStatusChange?.("🎙️ Microphone access granted");
      return true;
    } catch (e: any) {
      this.callbacks.onError?.("❌ Microphone access denied");
      return false;
    }
  }

  async connect(connectOptions?: { preferredTopicId?: string | null }): Promise<boolean> {
    const MAX_RETRIES = 3;
    let attempt = 0;

    if (!this.audioStream) {
      const ok = await this.initializeMic();
      if (!ok) return false;
    }

    // Validate configuration before proceeding
    if (!this.config.ephemeralUrl) {
      this.callbacks.onError?.("❌ Ephemeral URL not configured");
      return false;
    }

    if (!this.config.sessionId) {
      console.warn('[VoiceAgent] Warning: No sessionId provided, conversation tracking will be disabled');
    }

    while (attempt < MAX_RETRIES) {
      attempt++;
      this.firstAiAudioLogged = false;
      this.processedToolCallIds.clear();
      try {
        if (attempt > 1) {
          this.callbacks.onStatusChange?.(`🔄 Retrying connection (attempt ${attempt}/${MAX_RETRIES})...`);
          // Wait a bit before retrying (exponential backoff or simple delay)
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt - 1)));
        }

        // 1) Fetch ephemeral token + initial data
        const epUrl = this.config.ephemeralUrl;
        this.callbacks.onStatusChange?.("Requesting ephemeral token");
        // Use userId from config if available (passed from TaskTwo.tsx), otherwise try token
        let userId = this.config.userId || getUserIdFromToken();
        
        // Fallback: try to get guest user ID from localStorage if not found yet
        if (!userId) {
          try {
            const guestUser = getStoredGuestExamUser();
            if (guestUser?.user_id) {
              userId = guestUser.user_id;
              console.log('[VoiceAgent] Found guest user ID in localStorage:', userId);
            }
          } catch (e) {
            console.warn('[VoiceAgent] Error retrieving guest user from storage:', e);
          }
        }
        const guestId = getGuestId();
        const goalLevel = getGoalLevel();
        const sessionId = getSessionId();
        // Check if this is a retry attempt and get topic preference
        const isRetryAttempt = localStorage.getItem('current_task_attempt_id');
        const useSameTopic = localStorage.getItem('task2_retry_use_same_topic') === 'true';
        const preferredTopicId = connectOptions?.preferredTopicId;

        // Ensure we have a valid request body
        const requestBody = {
          sessionId: sessionId,
          userId: userId,
          guest_id: guestId,
          difficultyLevel: goalLevel || null,
          useSameTopic: isRetryAttempt ? useSameTopic : undefined, // Only send if it's a retry
          preferredTopicId: preferredTopicId || undefined, // Send preferred topic ID if available
        };
        const resp = await fetch(epUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        });
        if (!resp.ok) {
          const text = await resp.text().catch(() => '');
          throw new Error(`Failed to fetch ephemeral token: ${resp.status} ${text}`);
        }
        const initJson = await resp.json();
        console.log("INIT_JSON_RAW", initJson);

        const clientSecret = extractRealtimeClientSecret(initJson as Record<string, unknown>);
        if (!clientSecret) throw new Error('Ephemeral client secret missing (expected token, value, or client_secret.value)');

        this.realtimeModel = extractRealtimeModel(
          initJson as Record<string, unknown>,
          this.config.model || import.meta.env.VITE_OPENAI_REALTIME_MODEL || DEFAULT_REALTIME_MODEL
        );
        this.sessionModelFromServer = this.realtimeModel;

        console.log('[VoiceAgent][GA] Model:', this.realtimeModel);
        console.log('[VoiceAgent][GA] Client secret received:', maskSecret(clientSecret));

        const topic = initJson?.topic || undefined;
        const topicId = captureVoiceAgentInitTopicId(initJson);
        const systemPrompt = initJson?.systemPrompt || initJson?.system_prompt || undefined;
        const guardrails = initJson?.guardrails || undefined;
        const topicAudio = initJson?.topicAudio || undefined;
        if (topic) this.initialTopic = topic;
        if (topicId) {
          this.initialTopicId = topicId;
          this.taskTopicId = topicId;
        }
        if (systemPrompt) this.initialSystemPrompt = systemPrompt;
        if (guardrails) this.initialGuardrails = guardrails;

        // Store topicId in localStorage for Task 2
        if (topicId) {
          try {
            localStorage.setItem(TASK2_LOCAL_STORAGE_TOPIC_KEY, topicId);
            console.log('✅ Topic ID stored in localStorage (Task 2):', topicId);
          } catch (err) {
            console.warn('⚠️ Failed to store topic ID in localStorage:', err);
          }
        }

        if (topic || topicId || systemPrompt || guardrails || topicAudio) {
          this.callbacks.onInitialData?.({ topic, topicId, systemPrompt, guardrails, topicAudio });
        }

        // 2) Setup RTCPeerConnection and media
        const pc = new RTCPeerConnection();
        this.pc = pc;
        pc.oniceconnectionstatechange = () => { };
        // @ts-ignore
        pc.onconnectionstatechange = () => { };
        pc.onsignalingstatechange = () => { };
        // @ts-ignore
        pc.onicecandidateerror = (e: any) => { };

        // Receive remote audio
        try { pc.addTransceiver('audio', { direction: 'recvonly' }); } catch { }
        pc.ontrack = (event) => {
          const [stream] = event.streams;
          if (!this.remoteAudioEl) {
            this.remoteAudioEl = new Audio();
            this.remoteAudioEl.autoplay = true;
            // @ts-ignore playsInline supported at runtime
            this.remoteAudioEl.playsInline = true;
            this.remoteAudioEl.setAttribute('playsinline', 'true');
            this.remoteAudioEl.style.display = 'none';
            try { document.body.appendChild(this.remoteAudioEl); } catch { }

            // Add error handling for audio element
            this.remoteAudioEl.onerror = (e) => {
              console.warn('[VoiceAgent] Remote audio error:', e);
              this.isAssistantAudioActive = false;
              this.blockNormalReplies = false;
            };

            this.remoteAudioEl.onended = () => {
              console.log('[VoiceAgent] Remote audio ended');
              this.isAssistantAudioActive = false;
              this.blockNormalReplies = false;
            };
          }
          this.remoteAudioEl.srcObject = stream;
          // Volume must be between 0.0 and 1.0. 
          this.remoteAudioEl.volume = 1.0; 
          this.remoteAudioEl.muted = false;

          // Ensure audio is ready before playing
          this.remoteAudioEl.load();
          const p = this.remoteAudioEl.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {
              console.log('[VoiceAgent] Remote audio started playing');
            }).catch((err) => {
              console.warn('[VoiceAgent] Remote audio play failed:', err);
              this.isAssistantAudioActive = false;
              this.blockNormalReplies = false;
            });
          }
        };

        // Add mic track
        if (this.audioStream) {
          for (const track of this.audioStream.getTracks()) pc.addTrack(track, this.audioStream);
        }

        // 3) Data channel for events and tool calls
        const dc = pc.createDataChannel('oai-events');
        const originalSend = dc.send.bind(dc);
        dc.send = (data: string) => {
          return originalSend(data);
        };

        this.dc = dc;
        dc.onopen = () => {
          if (this.ended) return; // guard
          this.callbacks.onStatusChange?.("Realtime channel open");
          console.log('[VoiceAgent][GA] Realtime data channel opened');
          try {
            const sys = this.initialSystemPrompt || '';
            const topicExtra = this.initialTopic ? buildTask2TopicExtra(this.initialTopic) : '';
            const guardrails = this.initialGuardrails || '';
            const baseInstructions = [sys, topicExtra, guardrails].filter(Boolean).join('\n\n');
            const msg = buildGaSessionUpdatePayload({
              model: this.realtimeModel,
              instructions: baseInstructions || undefined,
              voice: this.config.voice,
              tools: buildTask2RealtimeTools(),
            });
            this.dc?.send(JSON.stringify(msg));
            console.log(`[VoiceAgent][GA] session.update sent — instructions length: ${(msg.session as { instructions?: string })?.instructions?.length ?? 'NONE'}`);
            console.log('[VoiceAgent][GA] session.update sent (instructions + tools + VAD)');
          } catch (e) {
            console.warn('[VoiceAgent][GA] session.update failed:', e);
          }
        };

        dc.onerror = (e) => this.callbacks.onStatusChange?.("Realtime channel error");
        dc.onclose = () => this.callbacks.onStatusChange?.("Realtime channel closed");
        dc.onmessage = (e) => this.handleDataMessage(e);

        // 4) SDP exchange with OpenAI
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        console.log('[VoiceAgent][GA] SDP POST', REALTIME_CALLS_URL, 'model=', this.realtimeModel);
        const sdpResp = await fetch(REALTIME_CALLS_URL, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${clientSecret}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp || '',
        });
        console.log('[VoiceAgent][GA] SDP response status:', sdpResp.status);
        if (!sdpResp.ok) {
          const sdpErr = await sdpResp.text().catch(() => '');
          console.error('[VoiceAgent][GA] SDP error body:', sdpErr);
          throw new Error(`Realtime SDP negotiation failed: ${sdpResp.status} ${sdpErr}`);
        }
        const answer = await sdpResp.text();
        await pc.setRemoteDescription({ type: 'answer', sdp: answer });

        this.callbacks.onStatusChange?.("Connected to Realtime");
        console.log('[VoiceAgent][GA] WebRTC connected (SDP answer applied)');

        // Start periodic audio health check
        setInterval(() => {
          if (!this.ended) {
            this.checkAudioHealth();
          }
        }, 2000); // Check every 2 seconds

        return true;
      } catch (err: any) {
        console.error(`[VoiceAgent] Connection attempt ${attempt} failed:`, err);
        if (attempt >= MAX_RETRIES) {
          this.callbacks.onError?.(`❌ Realtime connection failed after ${MAX_RETRIES} attempts: ${err?.message || err}`);
          return false;
        }
        // Cleanup resources before retrying
        if (this.pc) { try { this.pc.close(); } catch { } this.pc = null; }
        if (this.remoteAudioEl) { try { this.remoteAudioEl.pause(); } catch { } this.remoteAudioEl = null; }
      }
    }
    return false;
  }

  stop(): void {
    this.ended = true;
    if (this.toolContinuePromptTimer) { window.clearTimeout(this.toolContinuePromptTimer); this.toolContinuePromptTimer = null; }
    if (this.toolTaskEndingTimer) { window.clearTimeout(this.toolTaskEndingTimer); this.toolTaskEndingTimer = null; }
    if (this.responseWatchdogTimer) { window.clearTimeout(this.responseWatchdogTimer); this.responseWatchdogTimer = null; }
    if (this.assistantAudioWatchdogTimer) { window.clearTimeout(this.assistantAudioWatchdogTimer); this.assistantAudioWatchdogTimer = null; }
    if (this.assistantAudioEndedDelayTimer) { window.clearTimeout(this.assistantAudioEndedDelayTimer); this.assistantAudioEndedDelayTimer = null; }
    if (this.userResponseDebounceTimer) { window.clearTimeout(this.userResponseDebounceTimer); this.userResponseDebounceTimer = null; }
    this.continuePromptTriggered = false;
    this.continuePromptUsedOnce = false; // NEW: Reset the one-time flag on stop
    this.continuePromptFullyPlayed = false; // NEW: Reset the fully played flag on stop
    this.isAssistantAudioActive = false;
    this.blockNormalReplies = false;

    if (this.pc) { try { this.pc.close(); } catch { } this.pc = null; }
    if (this.remoteAudioEl) { try { this.remoteAudioEl.pause(); } catch { } this.remoteAudioEl = null; }
    if (this.localPromptAudioEl) { try { this.localPromptAudioEl.pause(); } catch { } this.localPromptAudioEl = null; }
    if (this.localPromptAudioSource) { try { this.localPromptAudioSource.stop(); this.localPromptAudioSource.disconnect(); } catch { } this.localPromptAudioSource = null; }
    if (this.audioStream) { this.audioStream.getTracks().forEach(t => t.stop()); this.audioStream = null; }
  }

  setMicEnabled(enabled: boolean) {
    if (this.audioStream) this.audioStream.getAudioTracks().forEach(t => (t.enabled = enabled));
    this.callbacks.onStatusChange?.(enabled ? 'Mic enabled' : 'Mic disabled');
  }

  updateSessionVoice(voice: string) {
    if (!voice || !this.dc || this.dc.readyState !== 'open' || this.ended) return;
    try {
      const msg = buildGaSessionUpdatePayload({
        model: this.realtimeModel,
        voice,
        includeTools: false,
      });
      this.dc.send(JSON.stringify(msg));
    } catch { }
  }

  setRemainingSeconds(seconds: number) {
    const value = typeof seconds === 'number' && isFinite(seconds) ? Math.max(0, Math.floor(seconds)) : 0;
    this.remainingSeconds = value;
  }

  setTopicId(topicId: string) {
    this.taskTopicId = topicId;
  }

  getTaskTopicId(): string | undefined {
    return this.taskTopicId;
  }

  // Stop all audio including AI responses when timer expires
  stopAllAudio() {
    console.log('[VoiceAgent] Stopping all audio due to timer expiration');

    // Stop AI response if pending
    this.aiResponsePending = false;
    this.isAssistantAudioActive = false;
    this.blockNormalReplies = true;

    // Stop remote audio (AI responses) - completely stop and mute
    if (this.remoteAudioEl) {
      try {
        this.remoteAudioEl.pause();
        this.remoteAudioEl.currentTime = 0;
        this.remoteAudioEl.muted = true;
        this.remoteAudioEl.src = '';
        this.remoteAudioEl.srcObject = null;
      } catch (e) {
        console.warn('[VoiceAgent] Error stopping remote audio:', e);
      }
    }

    // Stop local audio (continuation prompts, etc.) - use the comprehensive version
    this.stopLocalAudioCompletely();

    // Clear all timers
    if (this.toolContinuePromptTimer) {
      window.clearTimeout(this.toolContinuePromptTimer);
      this.toolContinuePromptTimer = null;
    }
    if (this.toolTaskEndingTimer) {
      window.clearTimeout(this.toolTaskEndingTimer);
      this.toolTaskEndingTimer = null;
    }
    if (this.userResponseDebounceTimer) {
      window.clearTimeout(this.userResponseDebounceTimer);
      this.userResponseDebounceTimer = null;
    }
    if (this.assistantAudioWatchdogTimer) {
      window.clearTimeout(this.assistantAudioWatchdogTimer);
      this.assistantAudioWatchdogTimer = null;
    }
    if (this.responseWatchdogTimer) {
      window.clearTimeout(this.responseWatchdogTimer);
      this.responseWatchdogTimer = null;
    }

    // Mark as ended to prevent further responses
    this.ended = true;
    this.taskCompleted = true;
  }

  // Comprehensive audio cleanup for complete shutdown
  stopAllAudioCompletely() {
    console.log('[VoiceAgent] Complete audio shutdown');

    // Call the regular stopAllAudio first
    this.stopAllAudio();

    // Additional comprehensive cleanup
    try {
      // Stop all audio elements in the document
      const audioElements = document.querySelectorAll('audio');
      audioElements.forEach(audio => {
        try {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = true;
          audio.src = '';
          audio.srcObject = null;
        } catch (e) {
          console.warn('[VoiceAgent] Error stopping audio element:', e);
        }
      });

    } catch (e) {
      console.warn('[VoiceAgent] Error during comprehensive audio cleanup:', e);
    }
  }

  // Reset audio state to fix stuck audio issues
  private resetAudioState() {
    console.log('[VoiceAgent] Resetting audio state');
    this.isAssistantAudioActive = false;
    this.blockNormalReplies = false;
    this.aiResponsePending = false;

    if (this.remoteAudioEl) {
      try {
        this.remoteAudioEl.pause();
        this.remoteAudioEl.currentTime = 0;
        this.remoteAudioEl.muted = false;
      } catch (e) {
        console.warn('[VoiceAgent] Error resetting audio element:', e);
      }
    }

    // Clear audio watchdog
    if (this.assistantAudioWatchdogTimer) {
      window.clearTimeout(this.assistantAudioWatchdogTimer);
      this.assistantAudioWatchdogTimer = null;
    }
  }

  // Check for stuck audio states and fix them
  private checkAudioHealth() {
    if (this.isAssistantAudioActive && this.remoteAudioEl) {
      // Check if audio is actually playing
      if (this.remoteAudioEl.paused || this.remoteAudioEl.ended) {
        console.warn('[VoiceAgent] Audio marked as active but element is paused/ended - fixing state');
        this.resetAudioState();
      }
    }
  }

  private stopLocalAudio() {
    // Stop WebAudio source if active
    if (this.localPromptAudioSource) {
      try {
        this.localPromptAudioSource.stop();
        this.localPromptAudioSource.disconnect();
      } catch (e) {
        // WebAudio source already stopped or error
      }
      this.localPromptAudioSource = null;
    }

    // Stop HTMLAudio if active
    if (this.localPromptAudioEl) {
      try {
        this.localPromptAudioEl.pause();
        this.localPromptAudioEl.currentTime = 0;
      } catch (e) {
        // HTMLAudio stop error
      }
      this.localPromptAudioEl = null;
    }

    // Restore mic and remote audio immediately
    this.setMicEnabled(true);
    if (this.remoteAudioEl) {
      try {
        this.remoteAudioEl.muted = false;
        const p = this.remoteAudioEl.play();
        if (p && typeof p.then === 'function') p.catch(() => { });
      } catch { }
    }
    this.isLocalAudioActive = false;
  }

  // Complete stop of local audio without re-enabling anything (for timer expiration)
  private stopLocalAudioCompletely() {
    // Stop WebAudio source if active
    if (this.localPromptAudioSource) {
      try {
        this.localPromptAudioSource.stop();
        this.localPromptAudioSource.disconnect();
      } catch (e) {
        // WebAudio source already stopped or error
      }
      this.localPromptAudioSource = null;
    }

    // Stop HTMLAudio if active
    if (this.localPromptAudioEl) {
      try {
        this.localPromptAudioEl.pause();
        this.localPromptAudioEl.currentTime = 0;
        this.localPromptAudioEl.src = '';
      } catch (e) {
        // HTMLAudio stop error
      }
      this.localPromptAudioEl = null;
    }

    // DO NOT restore mic or remote audio - this is for complete shutdown
    this.isLocalAudioActive = false;
  }

  private playLocalAudio(url: string, onEnded?: () => void, onInterrupted?: () => void) {
    if (!url) return;
    if (isLiveExamRuntimeExiting() || this.ended || this.taskCompleted) return;
    try {
      if (this.localPromptAudioEl) { try { this.localPromptAudioEl.pause(); } catch { } this.localPromptAudioEl = null; }
      // Temporarily mute remote audio and mic while playing local audio
      if (this.remoteAudioEl) {
        try { this.remoteAudioEl.muted = true; } catch { }
      }
      this.setMicEnabled(false);
      this.isLocalAudioActive = true;

      const ctx: AudioContext | undefined = (window as Window & { __appAudioContext?: AudioContext }).__appAudioContext;
      void playTrackedLiveExamAudio({
        url,
        audioContext: ctx,
        onEnd: () => {
          this.isLocalAudioActive = false;
          this.localPromptAudioSource = null;
          if (isLiveExamRuntimeExiting()) {
            return;
          }
          if (!this.ended && !this.taskCompleted) {
            this.setMicEnabled(true);
            if (this.remoteAudioEl) {
              try { this.remoteAudioEl.muted = false; const p2 = this.remoteAudioEl.play(); if (p2 && typeof p2.then === 'function') p2.catch(() => { }); } catch { }
            }
          }
          if (onEnded) onEnded();
        },
      }).then((result) => {
        if (result === 'aborted' || result === 'failed') {
          this.isLocalAudioActive = false;
          this.localPromptAudioSource = null;
          if (onInterrupted && result === 'aborted') onInterrupted();
        }
      });
    } catch {
      this.isLocalAudioActive = false;
      this.localPromptAudioSource = null;
    }
  }

  private shouldBlockNewExaminerSpeech(): boolean {
    return shouldSuppressNewExaminerSpeech(this.remainingSeconds);
  }

  private shouldBlockExaminerSpeechAfterEndingClaim(): boolean {
    return isTask2EndingClaimed() || this.ended || this.taskCompleted;
  }

  /** Stop WebRTC/proactive audio so static Task 2 ending is the sole audible authority. */
  private prepareAuthoritativeTask2EndingAudio() {
    if (this.toolContinuePromptTimer) {
      window.clearTimeout(this.toolContinuePromptTimer);
      this.toolContinuePromptTimer = null;
    }
    if (this.isLocalAudioActive && this.lastAssistantOutputKind === 'continue_prompt') {
      this.stopLocalAudio();
    }
    this.continuePromptTriggered = true;
    this.continuationRearmBlocked = true;
    this.blockNormalReplies = true;
    this.cancelPendingModelResponse();
    if (this.remoteAudioEl) {
      try {
        this.remoteAudioEl.pause();
        this.remoteAudioEl.currentTime = 0;
        this.remoteAudioEl.muted = true;
      } catch { }
    }
    this.lastAssistantOutputKind = 'ending';
  }

  private cancelPendingModelResponse() {
    if (!this.dc || this.dc.readyState !== 'open' || this.ended) return;
    try {
      if (this.aiResponsePending || this.isAssistantAudioActive) {
        this.dc.send(JSON.stringify({ type: 'response.cancel' }));
      }
    } catch { }
    this.aiResponsePending = false;
    this.isAssistantAudioActive = false;
    this.currentTextBuffer = '';
    if (this.responseWatchdogTimer) {
      window.clearTimeout(this.responseWatchdogTimer);
      this.responseWatchdogTimer = null;
    }
  }

  private abortProactiveContinuationForLearnerWin() {
    this.stopLocalAudio();
    this.cancelPendingModelResponse();
    this.blockNormalReplies = false;
    this.continuePromptFullyPlayed = false;
    this.continuePromptTriggered = true;
    this.continuationRearmBlocked = true;
    this.awaitingPostContinuationDecision = false;
    if (this.toolContinuePromptTimer) {
      window.clearTimeout(this.toolContinuePromptTimer);
      this.toolContinuePromptTimer = null;
    }
    if (this.lastAssistantOutputKind !== 'ending' && this.toolTaskEndingTimer) {
      window.clearTimeout(this.toolTaskEndingTimer);
      this.toolTaskEndingTimer = null;
    }
    if (this.remoteAudioEl) {
      try {
        this.remoteAudioEl.pause();
        this.remoteAudioEl.currentTime = 0;
      } catch { }
    }
    this.isAssistantAudioActive = false;
    this.lastAssistantOutputKind = 'none';
  }

  speakText(text: string) {
    if (!text || !this.dc || this.dc.readyState !== 'open' || this.ended) return false;
    if (this.shouldBlockNewExaminerSpeech()) return false;
    if (this.aiResponsePending) return false;
    try {
      this.aiResponsePending = true;
      const payload = { type: 'response.create', response: { output_modalities: ['audio', 'text'], instructions: text } } as any;
      this.dc.send(JSON.stringify(payload));
      return true;
    } catch {
      this.aiResponsePending = false;
      return false;
    }
  }
 
    async handleUserSpeech(interim: string, finalText: string) {
    if (this.ended) return;

    if (this.lastAssistantOutputKind === 'ending' && (this.isLocalAudioActive || this.isAssistantAudioActive)) {
      return;
    }

    const proactiveContinuation =
      this.lastAssistantOutputKind === 'continue_prompt' || this.isLocalAudioActive;

    if (
      shouldDropLearnerSpeechDuringAssistantAudio(
        this.isAssistantAudioActive,
        this.lastAssistantOutputKind,
        this.isLocalAudioActive,
      )
    ) {
      return;
    }

    if (proactiveContinuation && (interim || finalText)) {
      this.abortProactiveContinuationForLearnerWin();
    } else if (this.isLocalAudioActive && this.lastAssistantOutputKind === 'continue_prompt') {
      this.abortProactiveContinuationForLearnerWin();
    }
    // Pause remote audio on user speech to avoid overlap
    if ((interim || finalText) && this.remoteAudioEl) {
      try {
        this.remoteAudioEl.pause();
        this.remoteAudioEl.currentTime = 0;
      } catch { }
    }

    // Reset audio state if user speaks while audio is stuck
    if ((interim || finalText) && this.isAssistantAudioActive) {
      console.log('[VoiceAgent] User spoke during assistant audio - resetting audio state');
      this.resetAudioState();
    }

    if (interim) {
      this.liveTranscript = interim;
      this.callbacks.onLiveTranscriptUpdate?.(interim);
      this.lastSpeechTime = Date.now();
      this.silenceGenerationId++;
      // CRITICAL: Reset ALL timers on ANY user speech activity (interim or final)
      if (this.userResponseDebounceTimer) {
        window.clearTimeout(this.userResponseDebounceTimer);
        this.userResponseDebounceTimer = null;
      }
      if (this.toolContinuePromptTimer) {
        window.clearTimeout(this.toolContinuePromptTimer);
        this.toolContinuePromptTimer = null;
      }
      if (this.toolTaskEndingTimer) {
        window.clearTimeout(this.toolTaskEndingTimer);
        this.toolTaskEndingTimer = null;
      }

      // User spoke during a new turn: allow future continuation prompts again
      this.continuePromptTriggered = false;
      this.blockNormalReplies = false;
      return;
    }

    if (finalText) {
      this.fullTranscript += (finalText + ' ');
      this.conversation.push({ role: 'user', text: finalText });

      this.callbacks.onTranscriptUpdate?.(this.fullTranscript);
      this.callbacks.onHistoryUpdated?.(this.conversation);

      // Update AI with current conversation history
      this.updateAIWithConversationHistory();
      this.lastSpeechTime = Date.now();
      this.silenceGenerationId++;

      // Update pending final and (re)start debounce window before replying
      this.pendingUserFinalText = finalText;
      if (this.userResponseDebounceTimer) { window.clearTimeout(this.userResponseDebounceTimer); this.userResponseDebounceTimer = null; }
      // If we are directly after a continuation prompt, decide whether to end or reset
      if (this.awaitingPostContinuationDecision && this.silenceGenerationId >= this.postContinuationWindowId) {
        this.awaitingPostContinuationDecision = false;
        if (this.isDenialAfterContinuation(finalText)) {
          // Do not reset; end the task with ending audio
          this.callbacks.onStatusChange?.('Ending due to user denial after continuation');
          this.lastAssistantOutputKind = 'ending';
          this.handleTool_endTask({ delay: 0, text: "C'est la fin de la tâche 2", url: 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task2-end.mp3' });
          return;
        }
      }

      // CRITICAL: If user interrupted the still-time audio, ensure we're not stuck in decision windows
      if (this.awaitingPostContinuationDecision) {
      this.awaitingPostContinuationDecision = false;
      this.continuePromptTriggered = false;
    }
      
      // Otherwise reset continuation so it can trigger again after the assistant's next reply
      this.continuePromptTriggered = false;

      // User spoke: cancel timers and allow normal replies again
      if (this.toolContinuePromptTimer) {
        window.clearTimeout(this.toolContinuePromptTimer);
        this.toolContinuePromptTimer = null;
      }
      if (this.toolTaskEndingTimer) {
        window.clearTimeout(this.toolTaskEndingTimer);
        this.toolTaskEndingTimer = null;
      }
      this.blockNormalReplies = false;

      // Otherwise, schedule model response only after sustained silence
      const genAtStart = this.silenceGenerationId;
      const debounceMs = 700; // require 700ms silence before replying
      this.userResponseDebounceTimer = window.setTimeout(() => {
        if (this.ended || this.taskCompleted) return;
        if (this.silenceGenerationId !== genAtStart) return; // user resumed speaking
        const sinceSpeech = Date.now() - this.lastSpeechTime;
        if (sinceSpeech >= debounceMs) {
          // Guard: still no assistant speaking or pending
          if (!this.aiResponsePending && !this.isAssistantAudioActive && !this.blockNormalReplies) {
            // NEW: Add additional delay to ensure Deepgram has finalized
            const additionalDelay = 1000; // 1 second additional delay
            setTimeout(() => {
              if (this.ended || this.taskCompleted) return;
              if (this.silenceGenerationId !== genAtStart) return; // user resumed speaking
              if (!this.aiResponsePending && !this.isAssistantAudioActive && !this.blockNormalReplies) {
                // this.createResponseForUser(this.pendingUserFinalText || finalText);
              }
            }, additionalDelay);
          }
        }
      }, debounceMs);
    }
  }

  private handleDataMessage(e: MessageEvent) {
    if (this.ended) return;
    try {
      const msg = JSON.parse(e.data);
      // Errors
      if (msg?.type === 'error' || msg?.error) {
        const errMsg = (msg?.error?.message || msg?.error || 'Realtime error');
        if (typeof errMsg === 'string') this.callbacks.onError?.(errMsg);
        this.aiResponsePending = false;
        this.currentTextBuffer = "";
        return;
      }

      // Mark response started
      if (msg.type === 'response.created') {
        const currentTime = new Date().toISOString();


        this.conversation.forEach((msg, index) => {
          console.log(`   ${index + 1}. [${msg.role.toUpperCase()}]: "${msg.text}"`);
        });

        this.aiResponsePending = true;
        this.currentTextBuffer = "";

        // Arm watchdog to recover if response never completes
        if (this.responseWatchdogTimer) { window.clearTimeout(this.responseWatchdogTimer); this.responseWatchdogTimer = null; }
        this.responseWatchdogTimer = window.setTimeout(() => {
          if (this.aiResponsePending) {
            this.aiResponsePending = false;
            this.blockNormalReplies = false;
          }
        }, 5000);
        return;
      }

      if (msg.type === 'session.created' || msg.type === 'session.updated') {
        console.log('[VoiceAgent][GA]', msg.type);
        return;
      }

      // Tool/function call streamed args
      if (
        msg.type === 'response.function_call.arguments.delta' ||
        msg.type === 'response.tool_call.arguments.delta' ||
        msg.type === 'response.function_call_arguments.delta'
      ) {
        const callId = msg.call_id || msg.id || msg.tool_call_id || '';
        const name = msg.name || (msg.function_call && msg.function_call.name) || (this.pendingToolCalls[callId]?.name) || '';
        const delta = typeof msg.delta === 'string' ? msg.delta : '';
        if (callId) {
          if (!this.pendingToolCalls[callId]) this.pendingToolCalls[callId] = { name: String(name || ''), args: '' };
          this.pendingToolCalls[callId].args += delta;
        }
        return;
      }

      if (msg.type === 'response.function_call.completed' || msg.type === 'response.tool_call.completed') {
        if (this.ended) return;
        const callId = msg.call_id || msg.id || msg.tool_call_id || '';
        const stored = callId ? this.pendingToolCalls[callId] : undefined;
        const name = (msg.name || (msg.function_call && msg.function_call.name) || (stored && stored.name) || '').trim();
        let argsStr = '';
        if (typeof msg.arguments === 'string' && msg.arguments) argsStr = msg.arguments;
        else if (stored && stored.args) argsStr = stored.args;
        if (callId) delete this.pendingToolCalls[callId];
        let args: any = {};
        try { if (argsStr) args = JSON.parse(argsStr); } catch { }
        if (this.ended || this.taskCompleted) { if (callId) this.sendToolOutput(callId, { status: 'ignored_task_ended' }); return; }
        if (name === 'end_task') this.handleTool_endTask(args, callId);
        else if (name === 'check_user_silence') this.handleTool_checkUserSilence(args, callId);
        else if (name === 'prompt_continue') this.handleTool_promptContinue(args, callId);
        else if (callId) this.sendToolOutput(callId, { status: 'ignored', name });
        return;
      }

        // Audio start/resume signals
        if (
          msg.type === 'output_audio_buffer.started' ||
          msg.type === 'response.audio.delta' ||
          msg.type === 'response.output_audio.delta' ||
          msg.type === 'response.created'
        ) {
          this.logFirstAiAudio(msg.type);
          if (this.remoteAudioEl) {
            this.remoteAudioEl.muted = false;
  
            // Ensure audio is ready and not paused
            if (this.remoteAudioEl.paused) {
              this.remoteAudioEl.load();
            }
  
            const p = this.remoteAudioEl.play();
            if (p && typeof p.then === 'function') {
              p.then(() => {
                console.log('[VoiceAgent] Remote audio resumed/started');
              }).catch((err) => {
                console.warn('[VoiceAgent] Remote audio play failed during resume:', err);
                this.isAssistantAudioActive = false;
                this.blockNormalReplies = false;
              });
            }
          }
  
          if (msg.type === 'output_audio_buffer.started') {
            console.log('[VoiceAgent] Audio buffer started - setting assistant audio active');
            this.isAssistantAudioActive = true;
  
            // 🔇 NEW: mute mic while assistant is speaking to reduce audio-input cost
            // We don't want to stream user audio to OpenAI while the examiner is talking.
            this.setMicEnabled(false);
  
            // While assistant is speaking, ignore continuation scheduling
            if (this.toolContinuePromptTimer) {
              window.clearTimeout(this.toolContinuePromptTimer);
              this.toolContinuePromptTimer = null;
            }
            // Arm audio watchdog in case audio never stops
            if (this.assistantAudioWatchdogTimer) {
              window.clearTimeout(this.assistantAudioWatchdogTimer);
              this.assistantAudioWatchdogTimer = null;
            }
            this.assistantAudioWatchdogTimer = window.setTimeout(() => {
              console.warn('[VoiceAgent] Audio watchdog triggered - forcing audio to inactive');
              if (this.isAssistantAudioActive) {
                this.isAssistantAudioActive = false;
                this.blockNormalReplies = false;
  
                // 🟢 Safety: re-enable mic if audio got stuck and watchdog fires
                if (!this.isLocalAudioActive) {
                  this.setMicEnabled(true);
                }
              }
            }, 8000); // Increased timeout to 8 seconds
          }
          return;
        }

      // Text streaming
      if (msg.type === 'response.output_text.delta') {
        const delta = typeof msg.delta === 'string' ? msg.delta : (typeof msg.text === 'string' ? msg.text : '');
        if (delta) {
          this.currentTextBuffer += delta;
        }
        if (delta) {
          this.callbacks.onAIStreamDelta?.(this.currentTextBuffer);
        }
        return;
      }

      // Audio transcript streaming (when model outputs only audio with transcript deltas)
      if (msg.type === 'response.audio_transcript.delta' || msg.type === 'response.output_audio_transcript.delta') {
        const delta = typeof msg.delta === 'string' ? msg.delta : '';
        if (delta) {
          this.currentTextBuffer += delta;
        }
        if (delta) {
          this.callbacks.onAIStreamDelta?.(this.currentTextBuffer);
        }
        return;
      }

      // Handle cancellations/interruptions to avoid getting stuck in pending state
      if (msg.type === 'response.canceled' || msg.type === 'response.cancelled' || msg.type === 'response.interrupted') {
        this.aiResponsePending = false;
        this.isAssistantAudioActive = false;
        this.currentTextBuffer = "";

        // Do not block replies after a cancel; allow user to continue
        this.blockNormalReplies = false;
        return;
      }

      if (msg.type === 'response.completed' || msg.type === 'response.done') {
        if (this.ended) return;
        this.handleGaFunctionCallsInResponseDone(msg);
        const text = (this.currentTextBuffer || '').trim();
        if (text) {
          const currentTime = new Date().toISOString();

          this.callbacks.onAIResponse?.({ text });
          this.conversation.push({ role: 'assistant', text });

          this.conversation.forEach((msg, index) => { });


          this.callbacks.onHistoryUpdated?.(this.conversation);

          // Update AI with current conversation history
          this.updateAIWithConversationHistory();

          if (this.config.sessionId) {
            // We'll store this when output_audio_buffer.stopped fires
          }
        }
        this.aiResponsePending = false;
        if (this.responseWatchdogTimer) { window.clearTimeout(this.responseWatchdogTimer); this.responseWatchdogTimer = null; }
        // Block any further normal replies until the user speaks again
        this.blockNormalReplies = true;
        return;
      }

      if (msg.type === 'output_audio_buffer.stopped') {
        console.log('[VoiceAgent][GA] Audio buffer stopped event received — NOT stopping WebRTC audio track');
        return;
      }
      if (msg.type === 'response.output_audio.done') {
        if (this.assistantAudioEndedDelayTimer) {
          window.clearTimeout(this.assistantAudioEndedDelayTimer);
          this.assistantAudioEndedDelayTimer = null;
        }
        const transcript = (this.currentTextBuffer || '').trim();
        const delayMs = Math.max(3000, (transcript?.length || 0) * 60);
        console.log('[VoiceAgent][GA] Delaying audio cleanup by', delayMs, 'ms for transcript length', transcript?.length);
        this.assistantAudioEndedDelayTimer = window.setTimeout(() => {
          this.assistantAudioEndedDelayTimer = null;
          if (this.ended) return;
          this.handleAssistantAudioEnded('response.output_audio.done');
        }, delayMs);
        return;
      }
    } catch { }
  }

  private handleAssistantAudioEnded(eventName: string, resetRemoteAudio = true) {
          if (this.ended) return;

          if (shouldClearContinuationRearmBlock(this.lastAssistantOutputKind, this.continuationRearmBlocked)) {
            this.continuationRearmBlocked = false;
          }

          console.log('[VoiceAgent][GA] handleAssistantAudioEnded called from:', eventName);
          console.log('[VoiceAgent] Audio buffer stopped - setting assistant audio inactive');
          this.callbacks.onAssistantCompleted?.();
          this.aiResponsePending = false;
          this.assistantCompletedAt = Date.now();
          this.isAssistantAudioActive = false;
          if (this.assistantAudioWatchdogTimer) {
            window.clearTimeout(this.assistantAudioWatchdogTimer);
            this.assistantAudioWatchdogTimer = null;
          }
          if (!this.isLocalAudioActive) {
            this.setMicEnabled(true);
          }
  
          if (resetRemoteAudio && this.remoteAudioEl) {
            try {
              this.remoteAudioEl.pause();
              this.remoteAudioEl.currentTime = 0;
            } catch (e) {
              console.warn('[VoiceAgent] Error resetting audio element:', e);
            }
          }
  
          // NEW: Store AI transcript to DB now that audio has fully played
          if (this.config.sessionId && this.currentTextBuffer) {
            const text = this.currentTextBuffer.trim();
            if (text) {
              this.storeTranscriptToBackend(text, 'assistant').then(success => {
                if (success) {
                  // Clear the text buffer after successful storage
                  this.currentTextBuffer = "";
                }
              });
            }
          }

        // Schedule continuation flow based on 10s silence window
        if (this.toolContinuePromptTimer) { window.clearTimeout(this.toolContinuePromptTimer); this.toolContinuePromptTimer = null; }

        // CRITICAL: Add a small delay before scheduling continuation to allow user speech to be detected
        setTimeout(() => {
          // Double-check that user hasn't started speaking during this brief delay
          const timeSinceLastSpeech = Date.now() - this.lastSpeechTime;

          if (timeSinceLastSpeech < 2000) { // If user spoke within last 2 seconds, don't schedule continuation
            return;
          }

          if (
            shouldScheduleContinuationPrompt(
              this.remainingSeconds,
              this.continuePromptUsedOnce,
              this.continuationRearmBlocked,
            )
          ) {
              this.handleTool_promptContinue({ delayMs: 10000 });
          } else if (this.remainingSeconds > 45) {
            if (this.continuePromptUsedOnce) {
              // Continuation already used once: directly end after 10s silence
              if (this.toolTaskEndingTimer) { window.clearTimeout(this.toolTaskEndingTimer); this.toolTaskEndingTimer = null; }
              const genAtStart = this.silenceGenerationId;
              this.blockNormalReplies = false;
              this.toolTaskEndingTimer = window.setTimeout(() => {
                if (this.ended || this.taskCompleted) {
                  return;
                }
                if (this.silenceGenerationId !== genAtStart) {
                  this.blockNormalReplies = false;
                  return;
                }
                const silentFor = Date.now() - this.lastSpeechTime;
                if (silentFor >= 10000) {
                  this.callbacks.onStatusChange?.('Ending due to silence (continuation already used)');
                  this.lastAssistantOutputKind = 'ending';
                  this.handleTool_endTask({ delay: 0, text: "C'est la fin de la tâche 2", url: 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task2-end.mp3' });
                } else {
                  this.blockNormalReplies = false;
                }
              }, 10000);
            }
          } else {
            // Low time: wait 10s of silence, then end the task (no continuation)
            if (this.toolTaskEndingTimer) { window.clearTimeout(this.toolTaskEndingTimer); this.toolTaskEndingTimer = null; }
            const genAtStart = this.silenceGenerationId;
            // Allow normal replies during this window
            this.blockNormalReplies = false;
            this.toolTaskEndingTimer = window.setTimeout(() => {
              if (this.ended || this.taskCompleted) return;
              if (this.silenceGenerationId !== genAtStart) { this.blockNormalReplies = false; return; }
              const silentFor = Date.now() - this.lastSpeechTime;
              if (silentFor >= 10000) {
                this.callbacks.onStatusChange?.('Ending due to silence (low remaining time)');
                this.lastAssistantOutputKind = 'ending';
                this.handleTool_endTask({ delay: 0, text: "C'est la fin de la tâche 2", url: 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task2-end.mp3' });
              } else {
                this.blockNormalReplies = false;
              }
            }, 10000);
          }
        }, 500); // 500ms delay to allow user speech detection
  }

  private sendToolOutput(
    callId: string,
    output: any,
    options?: { createResponse?: boolean; toolName?: 'prompt_continue' | 'check_user_silence' | 'end_task' | 'thank_you_flow' },
  ) {
    if (!callId || !this.dc || this.dc.readyState !== 'open' || this.ended) return;
    try {
      this.dc.send(JSON.stringify({
        type: 'conversation.item.create',
        item: {
          type: 'function_call_output',
          call_id: callId,
          output: JSON.stringify(output || {}),
        },
      }));
      const createResponse =
        options?.createResponse ??
        (options?.toolName ? shouldCreateResponseForToolAck(options.toolName) : true);
      if (createResponse && !this.shouldBlockNewExaminerSpeech()) {
        this.dc.send(JSON.stringify({ type: 'response.create' }));
      }
    } catch { }
  }

  private logFirstAiAudio(source: string) {
    if (this.firstAiAudioLogged) return;
    this.firstAiAudioLogged = true;
    console.log('[VoiceAgent][GA] First AI audio response received via', source);
  }

  private dispatchToolCall(name: string, args: Record<string, unknown>, callId?: string) {
    if (this.ended || this.taskCompleted) {
      if (callId) this.sendToolOutput(callId, { status: 'ignored_task_ended' });
      return;
    }
    if (name === 'end_task') this.handleTool_endTask(args, callId);
    else if (name === 'check_user_silence') this.handleTool_checkUserSilence(args, callId);
    else if (name === 'prompt_continue') this.handleTool_promptContinue(args, callId);
    else if (callId) this.sendToolOutput(callId, { status: 'ignored', name });
  }

  private handleGaFunctionCallsInResponseDone(msg: any) {
    const output = msg?.response?.output;
    if (!Array.isArray(output)) return;
    for (const item of output) {
      if (item?.type !== 'function_call' || !item?.name) continue;
      const callId = item.call_id || item.id;
      if (callId && this.processedToolCallIds.has(callId)) continue;
      if (callId) this.processedToolCallIds.add(callId);
      let args: Record<string, unknown> = {};
      try {
        args = typeof item.arguments === 'string' ? JSON.parse(item.arguments) : (item.arguments || {});
      } catch { }
      this.dispatchToolCall(String(item.name).trim(), args, callId);
    }
  }

  // Tools
  private handleTool_promptContinue(args: any, callId?: string) {
    const currentTime = new Date().toISOString();
    console.log('='.repeat(60));
    console.log('🔧 [TOOL CALL] prompt_continue');

    if (this.ended || this.taskCompleted || this.aiResponsePending) {
      console.log('❌ Tool call ignored - ended:', this.ended, 'completed:', this.taskCompleted, 'pending:', this.aiResponsePending);
      return;
    }
    if (this.shouldBlockNewExaminerSpeech()) {
      if (callId) this.sendToolOutput(callId, { status: 'ignored_near_deadline' }, { createResponse: false, toolName: 'prompt_continue' });
      return;
    }
    const delayMs = Math.max(0, Number(args?.delayMs) || 0);

    // If a delay is requested, schedule the continuation after silence
    if (delayMs > 0) {
      if (callId) this.sendToolOutput(callId, { status: 'scheduled', delayMs }, { createResponse: false, toolName: 'prompt_continue' });
      if (this.toolContinuePromptTimer) { window.clearTimeout(this.toolContinuePromptTimer); this.toolContinuePromptTimer = null; }
      const genAtStart = this.silenceGenerationId;

      this.toolContinuePromptTimer = window.setTimeout(() => {
        if (this.ended || this.taskCompleted || this.aiResponsePending) return;

        const currentGenId = this.silenceGenerationId;
        const silentFor = Date.now() - this.lastSpeechTime;

        // Cancel if user spoke during the delay
        if (currentGenId !== genAtStart) {
          return;
        }

        if (silentFor >= delayMs && !this.continuePromptTriggered) {
          this.performContinuation();
        }
      }, delayMs);
      return;
    }

    // No delay requested: perform immediately (caller already observed silence window)
    if (!this.continuePromptTriggered) {
      if (callId) this.sendToolOutput(callId, { status: 'ok' }, { createResponse: false, toolName: 'prompt_continue' });
      this.performContinuation();
    } else {
      if (callId) this.sendToolOutput(callId, { status: 'ignored_already_triggered' }, { createResponse: false, toolName: 'prompt_continue' });
    }
  }

  private performContinuation() {
    const currentTime = new Date().toISOString();
    console.log('='.repeat(60));
    console.log('🎯 [TOOL EXECUTION] performContinuation');
    console.log('='.repeat(60));
    console.log('⏰ TIMESTAMP:', currentTime);
    console.log('⏱️ REMAINING TIME:', this.remainingSeconds, 'seconds');

    if (this.ended || this.taskCompleted || this.aiResponsePending || this.continuePromptTriggered) {
      console.log('❌ Continuation skipped due to state check');
      return;
    }
    if (this.shouldBlockNewExaminerSpeech()) {
      console.log('❌ Continuation skipped — near deadline suppression');
      return;
    }
    this.continuePromptTriggered = true;
    // Don't mark as used until audio fully plays
    this.callbacks.onStatusChange?.('Continuation prompt (static audio)');
    this.lastAssistantOutputKind = 'continue_prompt';
    // prevent normal replies until user speaks or flow completes
    this.blockNormalReplies = true;

    // Show text now (after delay) and play local static audio
    this.callbacks.onAIResponse?.({ text: "Souhaitez-vous ajouter autre chose ?" });
    const url = 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task2-continue-prompt-fr.mp3';

    if (this.toolTaskEndingTimer) { window.clearTimeout(this.toolTaskEndingTimer); this.toolTaskEndingTimer = null; }
    this.playLocalAudio(url, () => {
      // Only mark as used when audio fully completes
      this.continuePromptUsedOnce = true;
      this.continuePromptFullyPlayed = true;
      // After local audio, wait 10s of silence, then end
      if (this.toolTaskEndingTimer) { window.clearTimeout(this.toolTaskEndingTimer); this.toolTaskEndingTimer = null; }
      const genAtStart = this.silenceGenerationId;
      // Allow normal replies immediately after continuation audio ends
      this.blockNormalReplies = false;
      // Arm post-continuation decision window for the very next user final utterance
      this.awaitingPostContinuationDecision = true;
      this.postContinuationWindowId = this.silenceGenerationId;
      this.toolTaskEndingTimer = window.setTimeout(() => {
        if (this.ended || this.taskCompleted) return;
        // Cancel if user spoke after audio ended
        if (this.silenceGenerationId !== genAtStart) { this.blockNormalReplies = false; return; }
        const stillSilentFor = Date.now() - this.lastSpeechTime;
        if (stillSilentFor >= 10000) {
          this.callbacks.onStatusChange?.('Ending due to continued silence after continuation');
          this.lastAssistantOutputKind = 'ending';
          // Use end_task tool to play ending audio locally and then finalize
          this.handleTool_endTask({ delay: 0, text: "C'est la fin de la tâche 2", url: 'https://nluntjftnyzlxhatvvks.supabase.co/storage/v1/object/public/static-audio/task2-end.mp3' });
        } else {
          this.blockNormalReplies = false;
        }
      }, 10000);
    });
  }

  private handleTool_endTask(args: any, callId?: string) {
    console.log('='.repeat(60));
    console.log('🔧 [TOOL CALL] end_task');

    if (isLiveExamRuntimeExiting() || this.ended || this.taskCompleted) {
      console.log('❌ Tool call ignored - ended:', this.ended, 'completed:', this.taskCompleted);
      if (callId) this.sendToolOutput(callId, { status: 'ignored_task_ended' }, { createResponse: false });
      return;
    }
    if (!claimTask2Ending('tool')) {
      logTask2Ending('tool_ignored_already_claimed', { callId: callId ?? null });
      if (callId) this.sendToolOutput(callId, { status: 'ignored_ending_claimed' }, { createResponse: false });
      return;
    }
    const delayMs = Math.max(0, Number(args?.delay) || 0);
    const endText: string | undefined = typeof args?.text === 'string' ? args.text : undefined;
    const endUrl: string | undefined = typeof args?.url === 'string' ? args.url : undefined;
    logTask2Ending('tool_end_task', {
      source: 'playLocalAudio',
      url: endUrl ?? null,
      delayMs,
      callId: callId ?? null,
    });

    if (this.toolTaskEndingTimer) { window.clearTimeout(this.toolTaskEndingTimer); this.toolTaskEndingTimer = null; }
    if (this.toolContinuePromptTimer) { window.clearTimeout(this.toolContinuePromptTimer); this.toolContinuePromptTimer = null; }
    if (this.userResponseDebounceTimer) { window.clearTimeout(this.userResponseDebounceTimer); this.userResponseDebounceTimer = null; }

    this.prepareAuthoritativeTask2EndingAudio();

    if (callId) this.sendToolOutput(callId, { status: 'scheduled', delay: delayMs, hasAudio: !!endUrl }, { createResponse: false });

    this.toolTaskEndingTimer = window.setTimeout(() => {
      if (isLiveExamRuntimeExiting() || this.ended || this.taskCompleted) return;
      this.prepareAuthoritativeTask2EndingAudio();
      this.callbacks.onStatusChange?.('Playing task ending');
      const finalize = () => {
        this.taskCompleted = true;
        this.ended = true;
        this.callbacks.onTaskCompleted?.({ playedAudio: !!endUrl, topicId: this.taskTopicId });
        this.stop();

        // Check if this is a retry attempt
        const isRetryAttempt = localStorage.getItem('current_task_attempt_id');

        if (isRetryAttempt) {
          console.log('🎯 TASK 2 RETRY - AI ended task, navigating to Task2ProcessingScreen for retry processing');
          this.callbacks.onNavigate?.('/task2-processing');
        } else {
          console.log('🎯 TASK 2 - AI ended task, navigating to Task 3 (normal flow)');
          this.callbacks.onNavigate?.('/task-three');
        }
        // Let the TaskTwo component handle navigation instead of hard refresh
      };
      if (endText) this.callbacks.onAIResponse?.({ text: endText });
      if (endUrl) {
        // Play task ending audio locally as uninterruptible
        this.playLocalAudio(endUrl, () => finalize());
      } else {
        finalize();
      }
    }, delayMs);
  }

  private handleTool_checkUserSilence(args: any, callId?: string) {
    const currentTime = new Date().toISOString();
    console.log('='.repeat(60));
    console.log('🔧 [TOOL CALL] check_user_silence');

    if (this.ended || this.taskCompleted || this.aiResponsePending) {
      console.log('❌ Tool call ignored - ended:', this.ended, 'completed:', this.taskCompleted, 'pending:', this.aiResponsePending);
      return;
    }
    const durationMs = Math.max(0, Number(args?.duration) || 0);
    if (callId) this.sendToolOutput(callId, { status: 'observing', duration: durationMs });
    if (this.toolContinuePromptTimer) { window.clearTimeout(this.toolContinuePromptTimer); this.toolContinuePromptTimer = null; }
    const genAtStart = this.silenceGenerationId;
    this.toolContinuePromptTimer = window.setTimeout(() => {
      if (this.ended || this.taskCompleted || this.aiResponsePending) return;
      // Cancel if user spoke during wait
      if (this.silenceGenerationId !== genAtStart) return;
      const silentFor = Date.now() - this.lastSpeechTime;
      if (silentFor >= durationMs) {
        if (!this.continuePromptTriggered) {
          // First time: trigger the static continuation audio flow
          this.handleTool_promptContinue({});
        } else {
          // Already prompted once: do nothing here; the 10s-after-audio timer from prompt flow controls ending
        }
      }
    }, durationMs);
  }

  //closing (polite)
private isPoliteEnding(text: string): boolean {
  if (!text) return false;

  const t = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim();

  // ===============================
  // TIER 1 — UNIVERSAL SIMPLE ENDINGS
  // (A1–A2 users often use these)
  // ===============================
  const simpleEndings = [
    "merci",
    "merci beaucoup",
    "merci bien",
    "merci pour tout",
    "cest tout",
    "cest bon",
    "ca ira",
    "ca va",
    "ok merci",
    "parfait merci",
    "tres bien merci",
    "non merci",
    "je nai plus de questions",
    "pas dautres questions",
    "rien dautre",
    "aucune autre question"
  ];
  if (simpleEndings.some(s => t.includes(s))) return true;


  // ===============================
  // TIER 2 — LONG “MERCI” ENDINGS
  // (B1–B2–C1 users use these naturally)
  // ===============================
  if (t.startsWith("merci")) return true;  
  // This alone catches:
  // "merci pour les infos…"
  // "merci pour toutes ces precisions..."
  // "merci beaucoup pour votre aide..."
  // "merci, je regarderai ca..."

  const merciPatterns = [
    /merci.*je vais/,
    /merci.*je pourrai/,
    /merci.*je pourrais/,
    /merci.*je regarderai/,
    /merci.*je regarderais/,
    /merci.*je pense/,
    /merci.*je crois/,
    /merci.*ca maide/,
    /merci.*ca aide/,
    /merci.*ca a l air/,
    /merci.*avec ces/,
    /merci.*avec ca/,
    /merci.*cest parfait/,
    /merci.*ca suffit/
  ];
  if (merciPatterns.some(r => r.test(t))) return true;


  // ===============================
  // TIER 3 — C1–C2 STYLE POLITE ENDINGS
  // (complex, idiomatic, long sentences)
  // ===============================
  const advancedClosings = [
    /merci encore pour toutes ces informations/,
    /je vous remercie pour vos explications/,
    /je vous remercie pour toutes ces precisions/,
    /je pense que ce sera tout/,
    /je crois que ce sera tout/,
    /cela me suffit/,
    /ca me suffit/,
    /je suis satisfait/,
    /je suis satisfaite/,
    /nous avons fait le tour/,
    /on a fait le tour/,
    /on peut sarreter la/,
    /on peut terminer ici/
  ];
  if (advancedClosings.some(r => r.test(t))) return true;


  // ===============================
  // TIER 4 — MEGA CATCH-ALL REGEX
  // Handles ANY polite closure shape like:
  // “merci … je n’ai plus rien d’autre à demander”
  // ===============================
  const catchAll = [
    /merci.*(fini|tout|rien|suffit|tour|questions|assez)/,
    /(fini|terminer|assez|suffit).*(merci)?$/
  ];
  if (catchAll.some(r => r.test(t))) return true;


  // If nothing matches → not a polite ending
  return false;
}

  private isDenialAfterContinuation(text: string): boolean {
    if (!text) return false;
    const t = text
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .trim();

    const wc = t.split(/\s+/).filter(Boolean).length;
    const contains = (s: string) => t.includes(s);

    // Very short utterances (high-signal)
    if (wc <= 5) {
  if (
    // French: no / thanks
    t === 'non' || t === 'non.' ||
    t === 'non merci' || contains('non merci') ||
    t === 'merci' || t === 'merci.' ||
    t === 'merci beaucoup' || contains('merci beaucoup') ||
    t === 'merci bien' || contains('merci bien') ||
    t === 'merci pour tout' || contains('merci pour tout') ||

    // French: "that's all / nothing else"
    t === "c'est tout" || t === "cest tout" || contains("c'est tout") || contains("cest tout") ||
    t === "rien d'autre" || t === "rien dautre" || contains("rien d'autre") || contains("rien dautre") ||
    t === "ça suffit" || t === "ca suffit" || contains("ça suffit") || contains("ca suffit") ||
    t === "c'est bon" || t === "cest bon" || contains("c'est bon") || contains("cest bon") ||

    // English: no / thanks / that's all (rare but safe)
    t === 'no' || t === 'no.' ||
    t === 'thanks' || t === 'thanks.' ||
    t === "that's it" || t === "thats it" ||
    contains('no thanks') || contains('no thank you')
  ) return true;
}

    // French closures
    const fr = [
      "rien d'autre", "pas d'autres questions", "aucune autre question",
      "cest tout", "cest bon", "ca ira", "ca suffit",
      "on peut terminer", "on peut finir", "je nai plus de questions"
    ];

    // English safety (rare, but harmless)
    const en = [
      "nothing else", "no more", "that's it", "thats it", "that's all", "thats all",
      "i am done", "i'm done", "im done", "dont want to add", "don't want to add"
    ];

    return [...fr, ...en].some(p => contains(p));
  }


  public getSessionId(): string | undefined {
    return this.config.sessionId;
  }

  public getTopicId(): string | undefined {
    return this.initialTopicId;
  }

  public getConversationHistory(): Array<{ role: 'user' | 'assistant'; text: string }> {
    return [...this.conversation];
  }

    private updateAIWithConversationHistory(): void {
    if (this.isAssistantAudioActive) {
      console.log('[VoiceAgent][GA] ⏭️ Skipping session.update — assistant still speaking');
      return;
    }

    const currentTime = new Date().toISOString();
    console.log('⏰ TIMESTAMP:', currentTime);
    console.log('⏱️ REMAINING TIME:', this.remainingSeconds, 'seconds');
    console.log('📊 CONVERSATION LENGTH:', this.conversation.length, 'messages');
    console.log('='.repeat(80));
  
    if (!this.dc || this.dc.readyState !== 'open') {
      console.log('❌ AI update skipped - data channel not open');
      return;
    }
    if (this.conversation.length === 0) {
      console.log('❌ AI update skipped - no conversation history');
      return;
    }
  
    // We only log context now, we DO NOT send it as instructions
    const lastThree = this.conversation.slice(-3);
    const shortContext = lastThree
      .map(c => `${c.role}: ${c.text}`)
      .join(" | ");
  
    console.log('🧠 Short context (for debugging only, not sent as instructions):', shortContext);
  
    const msg = buildGaSessionUpdatePayload({
      model: this.realtimeModel,
      tools: buildTask2RealtimeTools(),
      includeTools: true,
    });
  
    console.log('📦 SESSION.UPDATE SENT (no instructions override):');
    console.log(JSON.stringify(msg, null, 2));
    console.log('='.repeat(80));
  
    this.dc.send(JSON.stringify(msg));
  }


  public getConfig(): VoiceAgentConfig {
    return { ...this.config };
  }

  // NEW: Get current timer status for debugging
  public getTimerStatus() {
    return {
      userResponseDebounceTimer: !!this.userResponseDebounceTimer,
      toolContinuePromptTimer: !!this.toolContinuePromptTimer,
      toolTaskEndingTimer: !!this.toolTaskEndingTimer,
      responseWatchdogTimer: !!this.responseWatchdogTimer,
      assistantAudioWatchdogTimer: !!this.assistantAudioWatchdogTimer,
      lastSpeechTime: this.lastSpeechTime,
      silenceGenerationId: this.silenceGenerationId,
      timeSinceLastSpeech: Date.now() - this.lastSpeechTime,
      blockNormalReplies: this.blockNormalReplies,
      isAssistantAudioActive: this.isAssistantAudioActive,
      aiResponsePending: this.aiResponsePending
    };
  }


  // NEW: Unified method to store transcripts to backend
  private async storeTranscriptToBackend(text: string, role: 'user' | 'assistant'): Promise<boolean> {
    if (!this.config.sessionId || !text) {
      console.warn('[VoiceAgent] Cannot store transcript - missing sessionId or text:', {
        sessionId: this.config.sessionId,
        textLength: text?.length,
        role
      });
      return false;
    }

    try {
      const payload: {
        sessionId: string;
        transcript: string;
        role: 'user' | 'assistant';
        user_id?: string;
        isRetryAttempt?: boolean;
      } = {
        sessionId: this.config.sessionId,
        transcript: text,
        role: role
      };

      // Add user_id if available
      if (this.config.userId) {
        payload.user_id = this.config.userId;
        console.log('[VoiceAgent] Including user_id in transcript payload:', this.config.userId);
      }

      // Add retry information if this is a retry attempt
      const isRetryAttempt = localStorage.getItem('current_task_attempt_id');
      if (isRetryAttempt) {
        payload.isRetryAttempt = true;
        console.log('[VoiceAgent] Including retry flag in transcript payload');
      }

      console.log('[VoiceAgent] Storing transcript to backend:', {
        role,
        text: text.substring(0, 50) + '...',
        sessionId: this.config.sessionId,
        userId: this.config.userId,
        hasUserId: !!this.config.userId
      });

      const response = await fetch(this.config.ephemeralUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('[VoiceAgent] Transcript stored successfully:', { role, result });
        return true;
      } else {
        const errorText = await response.text();
        console.warn('[VoiceAgent] Failed to store transcript:', { role, status: response.status, error: errorText });
        return false;
      }
    } catch (error) {
      console.error('[VoiceAgent] Error storing transcript:', { role, error });
      return false;
    }
  }

  // New method to explicitly close the global audio context (e.g. when exiting the exam)
  public cleanupGlobalAudio(): void {
    try {
      const ctx: AudioContext | undefined = (window as any).__appAudioContext;
      if (ctx && typeof ctx.close === 'function' && ctx.state !== 'closed') {
        console.log('🛑 [VoiceAgent] Explicitly closing global AudioContext');
        ctx.close().catch(() => { });
        (window as any).__appAudioContext = undefined;
      }
    } catch (e) {
      console.warn('[VoiceAgent] Error closing global audio context:', e);
    }
  }
}

export default VoiceAgentClient;



